import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

function ListingActionSidebar({ listing, onRequestSubmit, onStatusUpdate }) {
  const { user } = useAuth();
  const [requestNote, setRequestNote] = useState('');
  const [quantity, setQuantity] = useState(listing?.quantity || 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Check if the current user is the owner of this listing
  const isOwner = user?.id === listing?.donorId || user?.id === listing?.donor?.id;
  
  // Handle request submission
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onRequestSubmit({
        listing_id: listing?.id,
        quantity: quantity.toString(), // Keep as string as backend expects
        quantity_unit: listing?.unit || 'items', // Add required quantity_unit field
        notes: requestNote
      });
      
      // Show confirmation
      setShowConfirmation(true);
      setRequestNote('');
      
    } catch (error) {
      console.error('Error submitting request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle status update for listing owner
  const handleStatusUpdate = async (status) => {
    setIsSubmitting(true);
    
    try {
      await onStatusUpdate(listing?.id, status);
      
    } catch (error) {
      console.error('Error updating listing status:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render request confirmation
  if (showConfirmation) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <svg className="mx-auto h-12 w-12 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-3 text-lg font-medium text-green-800">Request Submitted!</h3>
        <p className="mt-2 text-sm text-green-600">
          Your request has been sent to the donor. You will receive a notification when they respond.
        </p>
        <button
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          onClick={() => setShowConfirmation(false)}
        >
          Done
        </button>
      </div>
    );
  }
  
  // Render owner controls
  if (isOwner) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900">Manage Listing</h3>
        
        <div className="mt-4 space-y-3">
          {/* Listing status */}
          <div>
            <span className="text-sm font-medium text-gray-500">Status:</span>{" "}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              listing?.status === 'available' ? 'bg-green-100 text-green-800' :
              listing?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              listing?.status === 'claimed' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {listing?.status || 'Available'}
            </span>
          </div>
          
          {/* Requests count */}
          <div className="text-sm text-gray-500">
            <span>{listing?.requestsCount || 0} requests received</span>
          </div>
          
          {/* Action buttons */}
          <div className="pt-4 space-y-2">
            <button
              type="button"
              onClick={() => window.location.href = `/edit-listing/${listing?.id}`}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Edit Listing
            </button>
            
            {listing?.status === 'available' && (
              <button
                type="button"
                onClick={() => handleStatusUpdate('unavailable')}
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Mark as Unavailable
              </button>
            )}
            
            {listing?.status === 'unavailable' && (
              <button
                type="button"
                onClick={() => handleStatusUpdate('available')}
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Mark as Available
              </button>
            )}
            
            <button
              type="button"
              onClick={() => window.location.href = `/requests/${listing?.id}`}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              View Requests
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render recipient request form
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900">Request This Food</h3>
      
      {/* Check if user is logged in and is a recipient */}
      {!user && (
        <div className="mt-3 text-sm text-gray-500">
          <p>You need to <a href="/login" className="text-primary-600 hover:text-primary-500">sign in</a> to request this food.</p>
        </div>
      )}
      
      {user && user.role !== 'recipient' && (
        <div className="mt-3 text-sm text-gray-500">
          <p>Only recipient organizations can request food donations.</p>
        </div>
      )}
      
      {user && user.role === 'recipient' && (
        <form className="mt-4 space-y-4" onSubmit={handleRequestSubmit}>
          <div>
            <label htmlFor="requestQuantity" className="block text-sm font-medium text-gray-700">
              Quantity Needed
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="number"
                name="requestQuantity"
                id="requestQuantity"
                min="0.1"
                step="0.1"
                max={listing?.quantity}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md sm:text-sm border-gray-300 focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                {listing?.unit || 'units'}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {listing?.quantity} {listing?.unit} total available
            </p>
          </div>
          
          <div>
            <label htmlFor="requestNote" className="block text-sm font-medium text-gray-700">
              Note to Donor (Optional)
            </label>
            <div className="mt-1">
              <textarea
                rows="3"
                name="requestNote"
                id="requestNote"
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="Introduce yourself and explain why you're requesting this food..."
                className="block w-full rounded-md sm:text-sm border-gray-300 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Request This Food'
              )}
            </button>
          </div>
          
          <div className="text-xs text-center text-gray-500 mt-2">
            The donor will be notified of your request and can approve or decline it.
          </div>
        </form>
      )}
    </div>
  );
}

export default ListingActionSidebar;
